import { httpsCallable } from 'firebase/functions'
import { functions } from 'fb/index'
import { apiClient } from 'api/client'
import { isRestStripeEnabled } from 'api/featureFlags'
import { toLegacyCallableError } from 'api/errorCompat'

/**
 * Stripe 関連 Cloud Functions の REST 化ラッパー。
 *
 * - REST フラグ ON: daseruno-api を叩き、レスポンスを旧 httpsCallable 結果と
 *   同じ形 (`{ data: ... }`) に整形して返す。エラーは toLegacyCallableError で
 *   callable 風に変換して throw。
 * - REST フラグ OFF: 従来通り httpsCallable を呼ぶ。
 *
 * いずれの経路でも戻り値は `{ data }` 形状なので、呼び出し側の
 * `.then((result) => result.data...)` は変更不要。
 */

type PlanType = 'standardMonth' | 'standardYear' | 'liteMonth' | 'liteYear'

type CreateSubscriptionParams = {
  email: string
  paymentMethodId: string
  plan: string
  shopId?: string | null
  storeId?: string | null
  paymentCustomerId?: string
  isStandard?: boolean
  isDebug?: boolean
}

export const createSubscription = async (
  params: CreateSubscriptionParams,
): Promise<{ data: { status: string; message: string } }> => {
  const {
    email,
    paymentMethodId,
    plan,
    shopId,
    storeId,
    paymentCustomerId,
    isStandard,
    isDebug,
  } = params
  if (isRestStripeEnabled()) {
    try {
      const res = await apiClient.post(
        '/v1/billings/subscriptions',
        {
          email,
          payment_method_id: paymentMethodId,
          plan: plan as PlanType,
          shop_document_id: shopId ?? '',
          store_document_id: storeId ?? '',
          ...(paymentCustomerId
            ? { payment_customer_id: paymentCustomerId }
            : {}),
        },
        isDebug ? { headers: { 'X-Debug': 'true' } } : undefined,
      )
      return { data: { status: res.data.status, message: res.data.message } }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(
    functions,
    isDebug
      ? 'apiCreateStripeSubscriptionDaserunoDebug'
      : 'apiCreateStripeSubscriptionDaseruno',
  )
  return callable({
    email,
    paymentMethodId,
    plan,
    shopId,
    storeId,
    ...(paymentCustomerId ? { paymentCustomerId } : {}),
    isStandard,
  }) as Promise<{ data: { status: string; message: string } }>
}

type UpdateSubscriptionParams = {
  storeId: string
  plan: string
}

export const updateSubscription = async (
  params: UpdateSubscriptionParams,
): Promise<{ data: { status: string; message: string } }> => {
  const { storeId, plan } = params
  if (isRestStripeEnabled()) {
    try {
      const res = await apiClient.patch('/v1/billings/subscriptions/me', {
        store_document_id: storeId,
        plan: plan as PlanType,
      })
      return { data: { status: res.data.status, message: res.data.message } }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(
    functions,
    'apiUpdateStripeSubscriptionDaseruno',
  )
  return callable({ storeId, plan }) as Promise<{
    data: { status: string; message: string }
  }>
}

export const getSubscription = async (
  subscriptionId: string,
): Promise<{ data: any }> => {
  if (isRestStripeEnabled()) {
    try {
      const res = await apiClient.get('/v1/billings/subscriptions/me', {
        params: { subscription_id: subscriptionId },
      })
      const d = res.data
      // 旧 Stripe Subscription 風に整形し、呼び出し側の
      // `result.data.items.data[0].price.id` が動くようにする。
      return {
        data: {
          id: d.id,
          status: d.status,
          customer: d.customer,
          current_period_start: d.current_period_start,
          current_period_end: d.current_period_end,
          cancel_at_period_end: d.cancel_at_period_end,
          items: { data: [{ price: { id: d.current_price_id } }] },
        },
      }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(functions, 'apiGetStripeSubscriptionDaseruno')
  return callable({ subscriptionId }) as Promise<{ data: any }>
}

export const getPaymentIntents = async (
  subscriptionId: string,
): Promise<{ data: { status: string; message: { data: any[] } } }> => {
  if (isRestStripeEnabled()) {
    try {
      const res = await apiClient.get('/v1/billings/payment-intents', {
        params: { subscription_id: subscriptionId },
      })
      // 呼び出し側 (PlanConfirmTemplate) は invoice から以下を読む:
      //   invoice.amount_due / invoice.subtotal (number)
      //   invoice.paid (boolean)
      //   invoice.created (unix seconds)
      //   invoice.lines.data[0].price.id (string)
      // REST の invoice は { id, created, total, currency, status,
      // invoice_pdf, hosted_invoice_url } しか持たないため、レンダーが参照する
      // フィールド名へマッピングする (template ではなく wrapper 側で吸収):
      //   total            → amount_due / subtotal
      //   status==='paid'  → paid
      //   lines.data[0].price.id は REST に price 情報が無いので空文字で補完
      //     (= プラン名表示は '' にフォールバックするが描画自体は壊れない)
      const invoices = ((res.data.invoices as any[]) ?? []).map((inv: any) => ({
        ...inv,
        amount_due: inv.total,
        subtotal: inv.total,
        paid: inv.status === 'paid',
        lines: { data: [{ price: { id: '' } }] },
      }))
      return {
        data: { status: res.data.status, message: { data: invoices } },
      }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(functions, 'apiGetStripePaymentIntent')
  return callable({ subscriptionId }) as Promise<{
    data: { status: string; message: { data: any[] } }
  }>
}

// ========================================
// Phase 3: 店舗 daseruno 契約情報取得
// ========================================

/**
 * GET /v1/billings/shop-subscription?store_document_id=...
 *
 * Firestore `shop_subscriptions` 1 doc 相当を MySQL から返す。
 * Web スライス ShopSubscriptionType と互換のシェイプを返す。
 * フラグ: isRestStripeEnabled (Stripe 課金ドメインとして扱う)
 */
export const fetchShopSubscription = async (
  storeDocumentId: string,
): Promise<{
  stripe_daseruno_subscription_id: string
  stripe_daseruno_subscription_schedule_id: string
  stripe_daseruno_old_subscription_ids: string[]
  is_daseruno_standard_plan: boolean
  is_sacri_station: boolean
  is_bill_payment: boolean
  stripe_subscription_id: string
  stripe_scheduled_subscription_id: string
} | null> => {
  if (!isRestStripeEnabled()) return null
  try {
    const res = await apiClient.get('/v1/billings/shop-subscription', {
      params: { store_document_id: storeDocumentId },
    })
    return res.data
  } catch {
    return null
  }
}

/**
 * PATCH /v1/billings/shop-subscription
 *
 * 請求書払いフラグの更新 (admin)。旧 Web updateIsBillPaymentByStoreId 相当。
 * subscriptions 行が無い場合の新規作成のため shopId (shop_document_id) を渡す。
 * フラグ判定 (isRestStripeEnabled) は呼び出し側 slice で行う。
 */
export const updateShopSubscriptionBillPayment = async (params: {
  storeId: string
  isBillPayment: boolean
  shopId?: string
}): Promise<void> => {
  await apiClient.patch('/v1/billings/shop-subscription', {
    store_document_id: params.storeId,
    is_bill_payment: params.isBillPayment,
    ...(params.shopId ? { shop_document_id: params.shopId } : {}),
  })
}

type UpdatePaymentMethodParams = {
  customerId: string
  paymentMethodId: string
  shopId: string
}

export const updatePaymentMethod = async (
  params: UpdatePaymentMethodParams,
): Promise<{ data: { status: string; message: string } }> => {
  const { customerId, paymentMethodId, shopId } = params
  if (isRestStripeEnabled()) {
    try {
      const res = await apiClient.patch(
        '/v1/billings/payment-methods',
        { customer_id: customerId, payment_method_id: paymentMethodId },
        { headers: { 'X-Shop-Document-Id': shopId } },
      )
      return { data: { status: res.data.status, message: res.data.message } }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(functions, 'apiUpdateStripePayment')
  return callable({ customerId, paymentMethodId, shopId }) as Promise<{
    data: { status: string; message: string }
  }>
}
